import { signInWithPopup, createUserWithEmailAndPassword } from 'firebase/auth'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { auth, googleAuthProvider } from '../../db'
import { FcGoogle } from 'react-icons/fc'
import {
  Divider,
  Flex,
  Input,
  Link,
  Text,
  useToast,
  Button
} from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import messages from './messages'
import InternalStandaloneLayout from '../../components/InternalStandaloneLayout'

export default function Signup() {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const toast = useToast()

  const handleGoogleLogin = () => {
    signInWithPopup(auth, googleAuthProvider)
      .then(() => {
        navigate('/dashboard')
        toast({
          title: formatMessage(messages.welcomeTitle),
          description: formatMessage(messages.welcomeDescription),
          status: 'success',
          duration: 4000,
          isClosable: true
        })
      })
      .catch((error: any) => {
        const errorMessage = error.message

        toast({
          title: formatMessage(messages.errorTitle),
          description: errorMessage,
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      })
  }

  const handleEmailSignup = async () => {
    if (password.length < 6) {
      toast({
        title: formatMessage(messages.errorTitle),
        description: formatMessage(messages.passwordTooShort),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
      return
    }

    if (password !== confirmPassword) {
      toast({
        title: formatMessage(messages.errorTitle),
        description: formatMessage(messages.passwordMismatch),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
      return
    }

    try {
      await createUserWithEmailAndPassword(auth, email, password)

      toast({
        title: formatMessage(messages.welcomeTitle),
        description: formatMessage(messages.welcomeDescription),
        status: 'success',
        duration: 4000,
        isClosable: true
      })

      navigate('/dashboard')
    } catch (error: any) {
      const errorMessage = error.message

      toast({
        title: formatMessage(messages.errorTitle),
        description: errorMessage,
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  }

  return (
    <InternalStandaloneLayout
      title={formatMessage(messages.welcomePageTitle)}
      description={formatMessage(messages.welcomePageSubtitle)}
    >
      <Flex flexDir="column" gap={5}>
        <Input
          placeholder={formatMessage(messages.emailPlaceholder)}
          size="md"
          onChange={(event) => {
            setEmail(event.target.value)
          }}
        />
        <Input
          placeholder={formatMessage(messages.passwordPlaceholder)}
          type="password"
          size="md"
          onChange={(event) => {
            setPassword(event.target.value)
          }}
        />
        <Input
          placeholder={formatMessage(messages.confirmPasswordPlaceholder)}
          type="password"
          size="md"
          onChange={(event) => {
            setConfirmPassword(event.target.value)
          }}
        />
        <Button
          colorScheme="gray"
          size="md"
          onClick={handleEmailSignup}
          mt={2}
          fontWeight="semibold"
        >
          {formatMessage(messages.signupButton)}
        </Button>

        <Flex align="center" my={2}>
          <Divider />
          <Text px={3} color="gray.400" fontSize="sm" fontWeight="medium">
            OR
          </Text>
          <Divider />
        </Flex>

        <Button
          onClick={handleGoogleLogin}
          variant="outline"
          size="md"
          w="full"
          leftIcon={<FcGoogle />}
          fontWeight="semibold"
        >
          {formatMessage(messages.googleSignupButton)}
        </Button>

        <Flex justifyContent="center" mt={4}>
          <Link
            color="gray.700"
            textDecoration="underline"
            fontWeight="medium"
            href="/login"
          >
            {formatMessage(messages.loginLink)}
          </Link>
        </Flex>
      </Flex>
    </InternalStandaloneLayout>
  )
}
